import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "antd";
import Table2 from "../../../components/Table";
import { useCandidates } from "../misc/useCandidate";
import { convertCandidatesToCandidatesType } from "../../../utility/candidateHelper";
import { CandidateType } from "../../../store/types/candidate";
import { delete_interview } from "../../../services/api/interview";
import { useAppDispatch } from "../../../store/hooks";
import DeleteConfirmModal from "../../../components/elements/DeleteConfirmModal";
import ModalNew from "../../../components/Modal/ModalNew";
import RescheduleModal from "../misc/RescheduleModal";
import { FilterTabs } from "../misc/FilterTabs";
import { ArrowLeft } from "lucide-react";

const columns2: {
  header: string;
  accessor: keyof CandidateType;
  sortable: boolean;
}[] = [
  { header: "S/N", accessor: "id", sortable: true },
  { header: "Name", accessor: "name", sortable: true },
  { header: "Email", accessor: "email", sortable: true },
  // { header: "Job Title", accessor: "job", sortable: true },
  { header: "Interview Date", accessor: "date", sortable: true },
  { header: "Start Time", accessor: "interviewDate", sortable: true },
  { header: "Expires", accessor: "expirationDate", sortable: true },
  { header: "IV Status", accessor: "invitationStatus", sortable: true },
  { header: "Status", accessor: "recommendationStatus", sortable: true },
  { header: "Interview Status", accessor: "interviewStatus", sortable: true },
];

interface InterviewCandidatesTableProps {
  jobId: string;
}

const InterviewCandidatesTable: React.FC<InterviewCandidatesTableProps> = ({
  jobId,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editModal, setEditModal] = useState({
    isOpen: false,
    candidate: null as CandidateType | null,
  });
  const [selectedStatus, setSelectedStatus] = useState<string>("all");

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    candidate: CandidateType | null;
  }>({
    isOpen: false,
    candidate: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const { candidates, loading, refetch } = useCandidates({
    page: "1",
    limit: "10",
    jobId,
    recommendationStatus: selectedStatus,
  });

  // const convertedData = convertCandidatesToCandidatesType(candidates);
  // Convert candidates data for table display and sort by most recent
  const convertedData = convertCandidatesToCandidatesType(
    [...candidates].sort(
      (a, b) =>
        new Date(b.interviewDate).getTime() -
        new Date(a.interviewDate).getTime(),
    ),
  );
  // const convertedData = React.useMemo(() => {
  //   return convertCandidatesToCandidatesType(candidates);
  // }, [candidates]);

  // Handlers
  const handleRowClick = (row: CandidateType) => {
    navigate(
      `/reports?interviewId=${row.interviewId}&candidateId=${row.candidateId}`,
    );
  };

  const handleEditClick = (row: CandidateType) => {
    setEditModal({
      isOpen: true,
      candidate: row,
    });
  };

  const handleRescheduleSuccess = () => {
    // Refetch the data to show updated information
    refetch();
    // Close the modal
    setEditModal({ isOpen: false, candidate: null });
  };

  const handleRowDelete = (row: CandidateType) => {
    setDeleteModal({
      isOpen: true,
      candidate: row,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteModal.candidate) {
      setIsDeleting(true);
      try {
        await delete_interview(
          dispatch,
          deleteModal.candidate.interviewId,
          () => {
            // Refetch the data to show updated list
            refetch();
            // Close the modal
            setDeleteModal({ isOpen: false, candidate: null });
          },
        );
      } catch (error) {
        // Error is already handled in the API call
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, candidate: null });
  };

  // Handle filter change
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const [showMore, setShowMore] = useState(false);

  return (
    <div className="w-full">
      <div className="mb-1">
        {/* Filter Tabs */}
        <FilterTabs
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          loading={loading}
        />
        <div className="flex items-center pt-2">
          <p className="text-lg font-[600]">List of interview for</p>
          {candidates?.slice(0, 1).map((item) => (
            <div key={item.interviewId} className="flex items-end">
              <p className="text-lg font-[600] pl-1"> {item.jobTitle}</p>
              <p className="text-sm text-gray-600 pl-1">
                {loading
                  ? "Loading..."
                  : `${candidates?.length || 0} interview(s)`}
              </p>
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : convertedData.length > 0 ? (
        <div>
          <div>
            {candidates?.slice(0, 1).map((item) => (
              <div key={item.interviewId}>
                <p className="font-[500] pb-1">Job Description:</p>
                <p className="pb-1">
                  {showMore
                    ? item.jobDescription
                    : `${item.jobDescription.substring(0, 100)}`}
                  {!showMore && "..."}
                  <button
                    onClick={() => setShowMore(!showMore)}
                    className="ml-1 text-blue-500 hover:text-blue-700"
                  >
                    {showMore ? " Read Less" : " Read More"}
                  </button>
                </p>
                {/* <p className="font-[500] pt-2 pb-1">Interview Questions:</p>
                <ul className="pb-1">
                  {item.interviewQuestions && item.interviewQuestions.map((question, index) => (
                    <li key={index}>{question}</li>
                  ))}
                </ul> */}
              </div>
            ))}
          </div>
          <Table2<CandidateType>
            data={convertedData}
            columns={columns2}
            onRowClick={handleRowClick}
            onEditClick={handleEditClick}
            onDeleteClick={handleRowDelete}
          />
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500">No interview found for this job</p>
          <p className="text-sm text-gray-400 mt-1">
            Interviews will appear here once interviews are conducted
          </p>
        </div>
      )}

      <DeleteConfirmModal
        candidate={deleteModal.candidate}
        isOpen={deleteModal.isOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={isDeleting}
      />

      {/* Reschedule Interview Modal */}
      <ModalNew
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, candidate: null })}
        title="Edit Candidate"
      >
        <RescheduleModal
          candidateData={editModal.candidate}
          isEditMode={true}
          onSuccess={handleRescheduleSuccess}
        />
      </ModalNew>
    </div>
  );
};

export default InterviewCandidatesTable;
